import {
  cloneElement,
  type FocusEventHandler,
  type ForwardedRef,
  forwardRef,
  memo,
  type MouseEventHandler,
  type ReactElement,
  type TouchEventHandler,
  useMemo,
  useRef,
} from 'react';

export type DetectType = 'click' | 'hover' | 'hover-hold' | 'hover-interact';

type AssignableRef<T> = ForwardedRef<T> | undefined;

function composeRefs<T>(...refs: AssignableRef<T>[]): (node: T | null) => void {
  return (node) => {
    for (const ref of refs) {
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
    }
  };
}

export type TriggerElementProps = {
  'aria-describedby'?: string;
  onBlur?: FocusEventHandler<HTMLElement>;
  onClick?: MouseEventHandler<HTMLElement>;
  onFocus?: FocusEventHandler<HTMLElement>;
  onMouseEnter?: MouseEventHandler<HTMLElement>;
  onMouseLeave?: MouseEventHandler<HTMLElement>;
  onTouchEnd?: TouchEventHandler<HTMLElement>;
};

type TriggerElement = ReactElement<TriggerElementProps> & {
  readonly ref?: ForwardedRef<HTMLElement>;
};

type TriggerEvent = {
  preventDefault: () => void;
};

type TriggerType = {
  children: TriggerElement;
  closeMenu?: () => unknown;
  detect?: DetectType;
  openMenu?: (event: TriggerEvent) => unknown;
  openOnFocus?: boolean;
  timeOutFunc?: () => unknown;
  tooltipId?: string;
};

export const Trigger = memo<TriggerType>(
  forwardRef<HTMLElement, TriggerType>(
    (
      {
        children,
        closeMenu,
        detect,
        openMenu,
        openOnFocus,
        timeOutFunc,
        tooltipId,
      },
      ref,
    ) => {
      const childRef = children.ref;
      const composedRef = useMemo(
        () => composeRefs(ref, childRef),
        [ref, childRef],
      );
      const isFocusedRef = useRef(false);
      const isHoveredRef = useRef(false);
      const ariaDescribedBy = [children.props['aria-describedby'], tooltipId]
        .filter(Boolean)
        .join(' ');
      const props: TriggerElementProps & {
        ref: (node: HTMLElement | null) => void;
      } = {
        'aria-describedby': ariaDescribedBy || undefined,
        onTouchEnd: openMenu,
        ref: composedRef,
      };

      switch (detect) {
        case 'click': {
          props.onClick = openMenu;
          break;
        }

        case 'hover': {
          if (openOnFocus) {
            props.onBlur = (event) => {
              isFocusedRef.current = false;
              children.props.onBlur?.(event);
              if (!isHoveredRef.current) {
                closeMenu?.();
              }
            };
            props.onFocus = (event) => {
              isFocusedRef.current = true;
              children.props.onFocus?.(event);
              openMenu?.(event);
            };
            props.onMouseEnter = (event) => {
              isHoveredRef.current = true;
              openMenu?.(event);
            };
            props.onMouseLeave = () => {
              isHoveredRef.current = false;
              if (!isFocusedRef.current) {
                closeMenu?.();
              }
            };
          } else {
            props.onMouseEnter = openMenu;
            props.onMouseLeave = closeMenu;
          }
          break;
        }

        case 'hover-hold': {
          props.onMouseEnter = openMenu;
          break;
        }

        case 'hover-interact': {
          props.onMouseEnter = openMenu;
          props.onMouseLeave = timeOutFunc;
          break;
        }

        default: {
          return null;
        }
      }

      return cloneElement(children, props);
    },
  ),
);
